import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { MdQrCodeScanner } from "react-icons/md";
import AdmitCardQrScanner from "../components/DataVerifier/QrScanner";
import { SCAN_TYPE } from "../components/DataVerifier/parseScannedQr";
import { ROLL_NO_PATTERN, ROLL_NO_QR_PUBLIC_KEYS, ROLL_NO_SIGN_CONTEXT, readRollNoLink } from "../utils/rollNoQrKeys";

// Public page the reference admit card's QR opens:
//   /r/<rollno>.<key version>.<signature>                → shows the roll number
//   /roll-number/<rollno>?k=<key version>&s=<signature>  → same (first link form)
//   /roll-number                                         → opens the QR scanner
// Shows ONLY the roll number. No API call, no store, no cookies, no login —
// the signature is checked in the browser (Web Crypto) against the public key
// in utils/rollNoQrKeys.js, so an edited or made-up roll number shows as invalid.

const STATUS = { CHECKING: "checking", VALID: "valid", INVALID: "invalid", UNSUPPORTED: "unsupported" };

const NOT_ROLL_NO_QR = "This QR is not from a State CET Cell admit card. Please scan the QR code on the admit card.";

const base64ToBytes = (value) => {
  const b64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
};

const verifyRollNo = async (rollno, version, signature) => {
  const spki = ROLL_NO_QR_PUBLIC_KEYS[version];
  if (!spki || !signature || !ROLL_NO_PATTERN.test(rollno)) return false;
  try {
    const key = await window.crypto.subtle.importKey(
      "spki",
      base64ToBytes(spki),
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"]
    );
    return await window.crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      key,
      base64ToBytes(signature),
      new TextEncoder().encode(`${ROLL_NO_SIGN_CONTEXT}|${version}|${rollno}`)
    );
  } catch (e) {
    // Malformed signature / key — treated as not issued by State CET Cell.
    return false;
  }
};

// Scanner parser: only the reference admit card's link is usable here (the
// classic card's sealed link and a bare roll number carry no signature). The
// scanned link is never opened — its parts are read and checked on this page,
// whatever site it names, so a QR cannot send the user elsewhere.
const parseRollNoLink = (text) => {
  try {
    const url = new URL((text || "").trim());
    const link = /^https?:$/.test(url.protocol) && readRollNoLink(url.pathname, url.searchParams);
    if (link) return { type: "rollno-link", value: link };
  } catch (e) {
    // Not a link.
  }
  return { type: SCAN_TYPE.INVALID };
};

const RollNumber = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // A /r/ path that does not split into three parts still shows as invalid.
  const link = readRollNoLink(pathname, searchParams) || (pathname.startsWith("/r/") ? { rollno: "-", k: "", s: "" } : null);
  const rollno = link?.rollno || "";
  const version = link?.k || "";
  const signature = link?.s || "";
  const [status, setStatus] = useState(STATUS.CHECKING);
  // No roll number in the link → open straight on the scanner.
  const [scanning, setScanning] = useState(!rollno);
  // Bumped to remount the scanner (it reads one code per mount).
  const [scanKey, setScanKey] = useState(0);

  useEffect(() => {
    if (!rollno) return undefined;
    let active = true;
    // Web Crypto only exists on HTTPS (and localhost).
    if (!window.crypto?.subtle) {
      setStatus(STATUS.UNSUPPORTED);
      return undefined;
    }
    setStatus(STATUS.CHECKING);
    verifyRollNo(rollno, version, signature).then((ok) => {
      if (active) setStatus(ok ? STATUS.VALID : STATUS.INVALID);
    });
    return () => {
      active = false;
    };
  }, [rollno, version, signature]);

  const startScan = () => {
    setScanKey((n) => n + 1);
    setScanning(true);
  };

  const handleScan = ({ value }) => {
    setScanning(false);
    navigate(`/r/${encodeURIComponent(value.rollno)}.${encodeURIComponent(value.k)}.${encodeURIComponent(value.s)}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f6fa",
        px: 2,
        py: 3,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 460,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          p: { xs: 2.5, sm: 4 },
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#555", mb: 3 }}>
          State Common Entrance Test Cell, Maharashtra
        </Typography>

        {scanning ? (
          <>
            <AdmitCardQrScanner key={scanKey} onScan={handleScan} parse={parseRollNoLink} invalidMessage={NOT_ROLL_NO_QR} />
            {rollno && (
              <Button onClick={() => setScanning(false)} sx={{ mt: 2, textTransform: "none" }}>
                Cancel
              </Button>
            )}
          </>
        ) : (
          <>
            {status === STATUS.CHECKING && <CircularProgress size={32} />}

            {status === STATUS.VALID && (
              <>
                <Typography sx={{ fontSize: 14, color: "#555" }}>Roll Number</Typography>
                <Typography sx={{ fontSize: 34, fontWeight: 700, letterSpacing: 1, my: 1, wordBreak: "break-all" }}>
                  {rollno}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1b7f3b" }}>
                  ✓ Issued by State CET Cell
                </Typography>
              </>
            )}

            {status === STATUS.INVALID && (
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#c62828" }}>
                ✗ This QR code is not valid
              </Typography>
            )}

            {status === STATUS.UNSUPPORTED && (
              <Typography sx={{ fontSize: 15, color: "#c62828" }}>
                This page must be opened over a secure (https) connection.
              </Typography>
            )}

            <Button
              variant="outlined"
              onClick={startScan}
              startIcon={<MdQrCodeScanner />}
              sx={{ mt: 3, borderRadius: "40px", textTransform: "none" }}
            >
              Scan another QR
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default RollNumber;
